about: |
    I am Denis, a teacher of things and a tinkerer of all sorts.

    I am currently working as a University Lecturer at Aalto University.
    I teach introductory programming and intro to programming languages.
    At the same time, I research using learning analytics to
    support self-regulation skills of first-year students and teaching responsible AI use.

    Over the years, I have been an avid game modder and have an interest in reverse engineering.
    Unity modding and tinkering with microcontrollers is close to my heart.

    If you happen to be at Otaniemi, you can find me at the Computer Science building, room A141.
    Come and have a coffee (or [book a meeting](https://bookings.cloud.microsoft/bookwithme/user/fd8d6cee633848ed8537e9f6bfcc5065@aalto.fi/meetingtype/snKzxOCWrka7Qdz4cDbCng2?anonymous&ismsaljsauthenabled&ep=mlink)) with me :)

skills:
    - title: Education
      items:
          - prefix: "2025"
            title: PhD in Education Technology
            description: University of Jyväskylä. Jyväskylä, Finland.
          - prefix: "2021"
            title: MSc in Information Technology; Education Technology line
            description: University of Jyväskylä. Jyväskylä, Finland.
          - prefix: "2019"
            title: BSc in Information Technology
            description: University of Jyväskylä. Jyväskylä, Finland.
    - title: Other skills
      items:
          - prefix: "2021"
            title: Subject teacher qualification for teaching information technology and mathematics
            description: University of Jyväskylä, Department of Teacher Education.
    - title: Languages
      items:
          - prefix: ""
            title: Finnish
            description: |
                *Excellent*, passed as mother tongue in the Matriculation Examination
          - prefix: ""
            title: English
            description: |
                *Excellent*, passed as A1 language in the Matriculation Examination
          - prefix: ""
            title: Russian
            description: |
                *Excellent*, passed as A1 language in the Matriculation Examination
          - prefix: ""
            title: Swedish
            description: |
                *Satisfactory*, studied as B1 language
    - title: Work
      items:
          - prefix: "Sep 2026 – "
            rawPrefix: true
            title: University Lecturer, Aalto University
            description: |
                - Responsible teacher for [CS-A1121/CS-A1123 Basics in Programming Y2](https://plus.cs.aalto.fi/y2), and [CS-C2171 Programming Languages](https://opencs.aalto.fi/en/courses/programming-languages)
          - prefix: "Aug 2021 – Aug 2026"
            rawPrefix: true
            title: University Teacher, University of Jyväskylä
            description: |
                - Managed and developed the [TIM](https://tim.jyu.fi/view/about/en-US) learning content management system.
                - Responsible teacher for [ITKP102 Programming 1](https://ohjelmointi1.it.jyu.fi/), [TIEP111 Programming 2](https://ohjelmointi2.it.jyu.fi/), and [ITKA2004 Databases and Data Management](https://tim.jyu.fi/view/kurssit/tie/itka2004/2024k/kotisivu).
                - Technical support and project supervisor for KOTES471 Learning analytics.
                - Supervised TIM-related student project in TIES405 Software Project course.
                - Designed exam materials for the Finnish digital entrance exams in Computer Science during years 2020–2024.
                - Designed tasks and organized [Upper Secondary School Competition in Information Technology](https://www.jyu.fi/fi/tapahtumat/keski-suomen-maakunnan-ja-lahikuntien-lukioiden-fysiikan-kemian-matematiikan-ja-tietotekniikan-1).
          - prefix: "Jan 2021 – Jul 2021"
            rawPrefix: true
            title: Technical Assistant, University of Jyväskylä
            description: |
                - Translated and evaluated learning materials for the [CodeInnova project](http://www.ci.unia.zam.pl/index.php/codeinnova-project).
                - Developed and maintained the [TIM](https://tim.jyu.fi/view/about/en-US) learning content management system.
                - Designed and monitored digital entrance exams for the Faculty of Information Technology.
          - prefix: "Mar 2020 – Aug 2020"
            rawPrefix: true
            title: "Research Assistant, University of Jyväskylä"
            description: |
                - Developed and maintained the [TIM](https://tim.jyu.fi/view/about/en-US) learning content management system.
                - Work focused on developing features to enable the systems's use in remote digital entrance exams.
          - prefix: "Nov 2020 – Dec 2020"
            rawPrefix: true
            title: "Teacher, Jyväskylä Educational Consortium Gradia"
            description: |
                - Taught an introductory web design course to Media and Visual Expression students.
                - Course contents: HTML5, CSS3, WordPress.
                - Demo of course materials: [Publishing on the web (in Finnish)](https://dezhidki.notion.site/Julkaiseminen-verkkosivulla-koti-demo-979cd3ad24574b71a26bc101c07855cc).
          - prefix: "Jan 2020 – Jun 2020"
            rawPrefix: true
            title: "Part-Time Teacher, University of Jyväskylä"
            description: |
                - Tutored students in the ITKP102 Programming 1 course.
    - title: Own publications
      items:
          - prefix: "Zhidkikh (2025)"
            rawPrefix: true
            title: "Self-Regulation and Student Success: Building a Data-Driven Approach to Understand Student Learning Behaviours in Computing Education"
            description: |
                PhD Thesis in Education Technology, https://urn.fi/URN:ISBN:978-952-86-0999-5
          - prefix: "Zhidkikh, Isomöttönen & Taipalus (2025)"
            rawPrefix: true
            title: "Understanding Self-Regulated Learning Behavior Among High and Low Dropout Risk Students During CS1: Combining Trace Logs, Dropout Prediction and Self-Reports"
            description: |
                Preprint, [arXiv:2506.09178](https://arxiv.org/abs/2506.09178) [cs.CY]
          - prefix: "Zhidkikh et al. (2024)"
            rawPrefix: true
            title: "Reproducing Predictive Learning Analytics in CS1: Toward Generalizable and Explainable Models for Enhancing Student Retention"
            description: |
                Journal of Learning Analytics, https://doi.org/10.18608/jla.2024.7979
          - prefix: "Zhidkikh, Saarela & Kärkkäinen (2023)"
            rawPrefix: true
            title: "Measuring self-regulated learning in a junior high school mathematics classroom: Combining aptitude and event measures in digital learning materials"
            description: |
                Journal of Computer Assisted Learning, https://doi.org/10.1111/jcal.12842
          - prefix: "Zhidkikh and Fagerlund et al. (2021)"
            rawPrefix: true
            title: '"CodeInnova": A Unified Framework for Teaching Programming and Computational Thinking In Primary Schools'
            description: |
                Poster in *Koli Calling ’21*, https://doi.org/10.1145/3488042.3489964
          - prefix: "Zhidkikh (2021)"
            rawPrefix: true
            title: "Investigating self-regulation in Finnish junior high school mathematics classes: a learning analytics case study"
            description: |
                Master Thesis in Education Technology, https://urn.fi/URN:NBN:fi:jyu-202106304100
    - title: Co-authored publications
      items:
          - prefix: "Isomöttönen & Zhidkikh (2025)"
            rawPrefix: true
            title: "Navigating through CS1: The Role of Self-Regulation and Supervision in Student Progress"
            description: |
                Preprint, [arXiv:2506.13461](https://arxiv.org/abs/2506.13461) [cs.CY]
          - prefix: "Taipalus, Grahn, ..., & Zhidkikh (2025)"
            rawPrefix: true
            title: "Novice Perceptions on Effective Elements of PostgreSQL Error Messages"
            description: |
                ACM Transactions on Computing Education, https://doi.org/10.1145/3732790

projects:
    - title: TIEP111 Programming 2 course materials
      description: |
          Together with Antti-Jussi Lakanen and Sami Sarsa, we performed a massive rewrite
          of course materials for the Programming 2 course taught at University of Jyväskylä.
          My main contributions to the material:

          - Initial docker-based development workflow for mdBook
          - GitOps setup and setup of the initial code executor server
          - Some extensions to mdBook: multitab code examples, custom accordion component
          - Contributions sections to chapters related to Java basics, interfaces, generics, Java Collection API and lambda expressions
          - Authored the JavaFX tutorial

          (as of writing this, the course materials have shifted to Zensical, although the overall structure is still the same)

          Released unter CC BY-SA 4.0, you are free to peruse the materials!
      links:
        - type: gh
          link: ohj-perus-jy/ohj2
        - type: direct
          link: https://ohjelmointi2.it.jyu.fi/
          name: Course homepage
    - title: The Interactive Material (TIM)
      description: |
          TIM (The Interactive Material) is a general-purpose learning content management
          system for producing highly interactive and open digital learning materials.
          It is tailored to teachers, students, material developers,
          and other content creators alike as an all-encompassing,
          centralised platform for creating and hosting document-based materials.

          TIM was originally created in 2014 by Vesa Lappalainen.
          I worked on TIM since 2021 as part of my work at the University of Jyväskylä.
          My main contributions to TIM are:

          - New theming system, allowing users to write and share themes.
          - New installation and management CLI.
          - Migrating TIM to GitHub and introducing a new CI pipeline.
          - Reorganization of the repo into a multi-repo structure.
          - Tools to create an manage TIM content locally.
          - Live status pages for users.

          **Languages used:** Python, TypeScript, Rust
      links:
          - type: gh
            link: TIM-JYU/TIM
          - type: gh
            link: JYU-DI/timsync
          - type: direct
            link: https://tim.jyu.fi/
            name: TIM front page
    - title: Unity Doorstop
      description: |
          A library to inject and bootstrap custom code into games running with Unity engine.
          Compared to most other solutions common in Unity modding communities, this tool is a simple copy-paste library that requires no edits to the game itself.

          On Windows, the library makes use of simple [DLL Hijacking](https://attack.mitre.org/techniques/T1574/001/) and [IAT hooking](https://www.ired.team/offensive-security/code-injection-process-injection/import-adress-table-iat-hooking).
          On Linux and macOS the library uses [`LD_PRELOAD`](http://www.goldsborough.me/c/low-level/kernel/2016/08/29/16-48-53-the_-ld_preload-_trick/) and `DYLD_LIBRARY_PATH` environment variables along with [plthook](https://github.com/kubo/plthook) library.

          **Languages used:** C
      image: doorstop
      links:
          - type: gh
            link: NeighTools/UnityDoorstop
          - type: gh
            link: NeighTools/UnityDoorstop.Unix
    - title: Tommy
      description: |
          A base library to read and write [TOML](https://github.com/toml-lang/toml) configuration file format.
          Uses a basic hand-made parser to read file format into a JSON-like object for easy traversing.  Can be used as a base library for high-level serializers.
          Includes a large unit test fixture for both reading and writing TOML.

          **Languages used:** C#
      image: tommy
      links:
          - type: gh
            link: dezhidki/Tommy
    - title: Escape Room Game Website for Mythbusters 2019 camp
      description: |
          Basic Escape Room Game website made for Myytinmurtajat 2019 (Mythbusters) camp organized at the University of Jyväskylä.
          The game was aimed at 7 to 9 year-old children.
          In it, the children solved puzzles related to computer science in order to obtain a code to unlock a prize.

          The project included two websites: a code input website and a blog which contained a "reorder year numbers" puzzle.

          **Languages used:** Python, JavaScript
          **Frameworks used:** Flask
      image: koodi
      links:
          - type: gh
            link: dezhidki/myytinmurtajat_it_19
          - type: direct
            link: https://koodi.deni.sh/
            name: koodi.deni.sh (code verification)
          - type: direct
            link: https://faniblogi.deni.sh/
            name: faniblogi.deni.sh (blog puzzle)
    - title: ZeroRPC.NET
      description: |
          This is a basic implementation of [zerorpc](https://www.zerorpc.io/) protocol for .NET.
          This was my test in porting larger projects from one language to another.

          ZeroRPC.NET allows to define a simple RPC interface over which processes can communicate with each other.
          ZeroRPC uses ZeroMQ for communication and MessagePack for data serialization.

          **Languages used:** C#
      links:
          - type: gh
            link: ghorsington/zerorpc-dotnet
    - title: Various LLM-assisted miniprojects
      description: |
          I have started using LLMs back in 2019 when the first version of GitHub Copilot was released into beta.
          Since then I have created various mini tools; some as quick solutions to small problems, some
          as a way to experiment with LLM use.
          Level of LLM use thus varies project-to-project. Below is a short list of all the stuff I have made (with
          a subjective estimation of LLM use using the [LAgAI scale](https://r.jyu.fi/lagai):

          - snap: A CLI tool that takes code files and outputs rendered SVG/EMF screenshots. Made because existing
                  either don't provide good highlight (e.g. carbon), don't provide SVG output or they are paid (codeshot).
                  Main use case was to easily add vectorized code screenshots to PowerPoint.
                  My first LLM-assisted project. (LAgAI: S70, L50)
          - slidev-addon-polls: An addon to [slidev](https://sli.dev/) that allows to easily embed live polls and MCQs into
                                slidev slides. Gives participants a voting page and integrates question control into preseneter view.
                                My first pretty much vibecoded project to test how well Claude integrates with GitHub PR system
                                and to add polls to my slides.
                                (LAgAI: S90, L90)

          These projects generally do not have license attached, primarily owing to the questionability of
          IPR with LLM-produced code. You are free to use the tools, but at your own risk.
      links:
        - type: gh
          link: dezhidki/slidev-addon-polls
        - type: gh
          link: dezhidki/snap
